/**
 * Live School du dimanche (migration 0329) — réglages d'un programme : live
 * activé, SKILLCARD légendaire HORS PACK gagnée par le podium, taille du
 * podium. Écriture admin directe sous RLS ; le trigger
 * `guard_live_prize_card` revérifie le lot en base.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { CardColorTheme, CardRarity } from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export const LIVE_PRIZE_TOP_N_CHOICES = [1, 3, 5, 10] as const;

export interface ProgrammeLiveSettings {
  live_enabled: boolean;
  live_prize_card_id: string | null;
  live_prize_top_n: number;
}

export interface LivePrizeCandidate {
  id: string;
  title: string;
  rarity: CardRarity;
  color_theme: CardColorTheme;
  creature: unknown;
  domain_label: string | null;
  levels: string[];
}

/** Lot valide = SKILLCARD légendaire rattachée à aucun pack. */
export function livePrizeCandidates<T extends { id: string; rarity: string }>(
  cards: readonly T[],
  packCardIds: ReadonlySet<string>,
): T[] {
  return cards.filter((c) => c.rarity === 'legendaire' && !packCardIds.has(c.id));
}

export function livePrizeErrorMessage(message: string): string {
  if (message.includes('live_prize_not_legendary')) {
    return 'Le lot doit être une SKILLCARD légendaire publiée.';
  }
  if (message.includes('live_prize_in_pack')) {
    return 'Cette SKILLCARD fait partie d’un pack : le lot doit être une carte hors pack.';
  }
  return 'L’enregistrement a échoué. Réessaie.';
}

export const programmeLiveKey = (programmeId: string) =>
  ['programme-live-settings', programmeId] as const;

export function useProgrammeLiveSettings(programmeId: string | null) {
  return useQuery({
    queryKey: programmeLiveKey(programmeId ?? ''),
    enabled: !!programmeId,
    queryFn: async (): Promise<ProgrammeLiveSettings> => {
      const { data, error } = await db
        .from('curriculum_programmes')
        .select('live_enabled, live_prize_card_id, live_prize_top_n')
        .eq('id', programmeId)
        .single();
      if (error) throw new Error(error.message);
      return data as ProgrammeLiveSettings;
    },
  });
}

/** SKILLCARDS légendaires publiées hors pack (candidates au lot). */
export function useLivePrizeCandidates(enabled = true) {
  return useQuery({
    queryKey: ['live-prize-candidates'],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<LivePrizeCandidate[]> => {
      const { data, error } = await db
        .from('cards')
        .select('id, title, rarity, color_theme, creature, domain_label, card_levels(level)')
        .eq('rarity', 'legendaire')
        .not('published_version', 'is', null)
        .neq('status', 'archived')
        .order('title');
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as (Omit<LivePrizeCandidate, 'levels'> & {
        card_levels: { level: string }[] | null;
      })[];
      const ids = rows.map((r) => r.id);
      const { data: inPack, error: packErr } = ids.length
        ? await db.from('curriculum_pack_cards').select('card_id').in('card_id', ids)
        : { data: [], error: null };
      if (packErr) throw new Error(packErr.message);
      const packIds = new Set((inPack ?? []).map((r) => (r as { card_id: string }).card_id));
      return livePrizeCandidates(
        rows.map((r) => ({ ...r, levels: (r.card_levels ?? []).map((l) => l.level) })),
        packIds,
      );
    },
  });
}

export function useSaveProgrammeLive(programmeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<ProgrammeLiveSettings>) => {
      const { error } = await db.from('curriculum_programmes').update(patch).eq('id', programmeId);
      if (error) throw new Error(livePrizeErrorMessage(error.message));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: programmeLiveKey(programmeId) }),
  });
}
